import { DeleteOutline } from "@mui/icons-material";
import { Collapse, Tooltip } from "@mui/material";
import useFieldCustomize from "components/playbook/hooks/useFieldCustomize";
import { MuiFlatpickr } from "components/summary/success-plan";
import { FIELD_ACTIVITY_STATUS, FIELD_PLAYCARD_STATUS, FIELD_TASK_STATUS } from "constant";
import React, { Dispatch, FC, SetStateAction, useEffect } from "react";
import { Control, Controller, UseFormSetValue, useForm } from "react-hook-form";
import { FieldState, translateRules, useStyles } from "../common";
import SharedFieldForm from "./SharedFieldForm";
import moment from "moment-timezone";

interface DatePickerProps {
  state: FieldState | undefined;
  control: Control;
  setValue: UseFormSetValue<any>;
  setFieldStatelist: React.Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
  readOnly?: boolean;
  handleInput: (
    value: string,
    setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>
  ) => void;
  enableValidate: boolean;
  showLimit?: boolean;
}

const DatePickerField: FC<DatePickerProps> = ({
  state,
  control,
  setFieldStatelist,
  readOnly = false,
  handleInput,
  enableValidate = true,
}) => {
  return (
    <Controller
      control={control}
      render={({ field, fieldState }) => (
        <>
          <MuiFlatpickr
            {...field}
            fullWidth
            value={field.value}
            onChange={(dates: any) => {
              const dateValue = dates[0] ? new Date(dates[0]).toISOString() : "";
              handleInput(dateValue, setFieldStatelist);
            }}
            placeholder={state?.placeholder.value}
            size="small"
            readonly={readOnly}
          />
          <div style={{ color: "#f44336", marginTop: "4px" }}>
            {fieldState.error ? (
              <span style={{ color: "#f44336", marginTop: "4px" }}>{fieldState.error.message}</span>
            ) : null}
          </div>
        </>
      )}
      name={state?.id ?? ""}
      defaultValue={state?.defaultValue ?? ""}
      rules={translateRules(state?.rules, enableValidate, state?.visible ?? true)}
    />
  );
};

const DatePickerWrapper: FC<{
  className: any;
  fieldId: string;
  state?: FieldState;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  readOnly: boolean;
  setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
}> = ({
  className,
  fieldId,
  state,
  control,
  setValue,
  enableValidate,
  readOnly,
  setFieldStateList,
  disabled = false,
}) => {
  const updateInputValue = (
    value: string,
    setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>
  ) => {
    setFieldStateList((items) =>
      items.map((item) => (item.id === fieldId ? { ...item, defaultValue: value } : item))
    );
    setValue(fieldId, value, { shouldValidate: true });
  };

  return (
    <div className={className}>
      <p
        style={{
          fontSize: 12,
          color: "#535759",
          fontWeight: 500,
          display: "flex",
        }}
      >
        {state?.rules.required?.value === "y" && (
          <span style={{ color: "#FF5F58", marginRight: 4 }}>*</span>
        )}
        {state?.label.value === "" ? "Label Name" : state?.label.value}
      </p>
      <DatePickerField
        state={state}
        setValue={setValue}
        control={control}
        setFieldStatelist={setFieldStateList}
        readOnly={readOnly}
        handleInput={updateInputValue}
        enableValidate={enableValidate}
        disabled={disabled}
      />
    </div>
  );
};

export const ConfigDatePickerComponent: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  disabled?: boolean;
}> = ({ fieldId, control, setValue, enableValidate }) => {
  const classes = useStyles();

  const { setFieldStateList, selectField, removeField, isCurrentField, getFieldState } =
    useFieldCustomize();

  const state = getFieldState(fieldId);

  return (
    <Tooltip
      title={
        <DeleteOutline
          className={classes.trashIcon}
          onClick={() => removeField(fieldId)}
        />
      }
      placement={"right-start"}
      classes={{ tooltip: classes.tooltip }}
    >
      <div onClick={() => selectField(fieldId)}>
        <DatePickerWrapper
          className={isCurrentField(fieldId) ? classes.selectedField : classes.unselectedInnerField}
          fieldId={fieldId}
          state={state}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          readOnly={true}
          setFieldStateList={setFieldStateList}
        />
      </div>
    </Tooltip>
  );
};

export const PlainDatePickerComponent: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  stateList: FieldState[];
  setStateList: Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
}> = ({
  fieldId,
  control,
  setValue,
  enableValidate,
  disabled = false,
  stateList,
  setStateList,
}) => {
  const classes = useStyles();

  /*   const { finalStateList, setFinalStateList } = useContext(
    CustomFieldOuterContext
  ); */

  const state = stateList.find((x) => x.id === fieldId);

  const notMapID = [FIELD_ACTIVITY_STATUS, FIELD_TASK_STATUS, FIELD_PLAYCARD_STATUS];

  useEffect(() => {
    if (state && (state?.visible ?? true)) {
      setValue(fieldId, state?.defaultValue, { shouldValidate: true });
    }
  }, [state]);

  useEffect(() => {
    if (!notMapID.includes(state?.id as string)) {
      if (!(state?.visible ?? true)) {
        setStateList((items) =>
          items.map((item) =>
            item.id === state?.id
              ? {
                  ...item,
                  defaultValue: state.isMultiple ? [] : "",
                }
              : item
          )
        );

        (state?.dependsBy ?? []).map((fieldId) =>
          setStateList((items) =>
            items.map((item) =>
              item.id === fieldId
                ? {
                    ...item,
                    defaultValue: item.isMultiple ? [] : "",
                    visible: false,
                  }
                : item
            )
          )
        );
      }
    }
  }, [state?.visible]);

  return (
    <Collapse
      in={state?.visible ?? true}
      style={{
        width: "100%",
        height: (state?.visible ?? true) ? "auto" : 0,
        overflow: "hidden",
      }}
    >
      <DatePickerWrapper
        className={classes.unselectedField}
        fieldId={fieldId}
        state={state}
        control={control}
        setValue={setValue}
        enableValidate={enableValidate}
        readOnly={false}
        setFieldStateList={setStateList}
        disabled={disabled}
      />
    </Collapse>
  );
};

export const DatePickerPropertyForm: FC<{ fieldId: string }> = ({ fieldId }) => {
  const { setFieldStateList, getFieldState } = useFieldCustomize();
  const state = getFieldState(fieldId);

  const { control, handleSubmit, setValue, unregister } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    // empty funciton
    setFieldStateList((items) =>
      items.map((item) => (item.id === state?.id ? { ...item, passValidate: true } : item))
    );
  };

  const onError = () => {
    // empty function
  };

  useEffect(() => {
    setValue("labelName", state?.label.value);
    state?.conditions?.map((c) => {
      setValue(c.ruleId, c.fieldId);
      setValue(`${c.ruleId}-values`, c.fieldValue);
    });
    handleSubmit(onSubmit, onError)();
  }, [state?.conditions, state?.label, state?.rules]);

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <SharedFieldForm
        formHeader="Date Picker"
        fieldId={fieldId}
        control={control}
        setValue={setValue}
        unregister={unregister}
        specComponent={<></>}
      />
    </form>
  );
};

export const DatePickerViewComponent: FC<{ state: FieldState }> = ({ state }) => {
  const classes = useStyles();
  const value = state.defaultValue ? moment(state.defaultValue).format("MM/DD/YYYY") : "";

  return (
    <>
      {(state?.visible ?? true) && (
        <div
          key={state.id}
          style={{
            width: "100%",
            lineHeight: "32px",
            marginTop: 8,
          }}
        >
          <div style={{ display: "block", width: "100%", marginLeft: 4 }}>
            <span className={classes.field}>
              {state.label.value ? state.label.value : "Label Name"}:
            </span>
          </div>
          <div
            style={{
              backgroundColor: "#F7F7F7",
              opacity: 0.8,
              borderRadius: 4,
              padding: 5,
            }}
          >
            <span>{value}</span>
          </div>
        </div>
      )}
    </>
  );
};
